import React from "react";
import {
  Typography,
  Button,
  Link,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Menu,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Link as RouterLink } from "react-router-dom";
import API from "../API";
import JyskRequestOperations from "../components/JyskRequestOperations";

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("tr-TR");
};

const getReceiptSrc = (image) =>
  image?.base64
    ? `data:${image.mime_type || "image/jpeg"};base64,${image.base64}`
    : "";

const getReceiptDisplayName = (image, index) => {
  if (!image?.name) return `Fiş Görseli ${index + 1}`;

  const filename = image.name.trim();
  if (!filename) return `Fiş Görseli ${index + 1}`;

  return filename;
};
const getBarcodeImageSrc = (barcode) =>
  barcode ? `https://cdn1.jysk.com/getimage/wd3.medium/${barcode}` : "";

const getShortId = (value) =>
  value ? value.substring(Math.max(value.length - 6, 0)).toUpperCase() : "-";

const getBarcodeSummary = (barcodes) => {
  const total = (barcodes || []).length;
  const found = (barcodes || []).filter((barcode) => barcode.found).length;

  if (total === 0) return "Barkod kaydı yok";

  return `${total} ürün · ${found} bulundu`;
};
const STATUS_STYLES = {
  stock_pending: {
    backgroundColor: "rgba(245, 158, 11, 0.14)",
    color: "#B45309",
  },
  stock_arrived: {
    backgroundColor: "rgba(14, 165, 233, 0.14)",
    color: "#0369A1",
  },
  order_created: {
    backgroundColor: "rgba(86, 84, 232, 0.14)",
    color: "#4338CA",
  },
  delivered: {
    backgroundColor: "rgba(16, 185, 129, 0.14)",
    color: "#047857",
  },
  cancelled: {
    backgroundColor: "rgba(239, 68, 68, 0.14)",
    color: "#B91C1C",
  },
};

const getStatusLabel = (value) => {
  if (!value) return "Beklemede";
  if (value === "stock_pending") return "Stok Bekliyor";
  if (value === "stock_arrived") return "Stok Geldi";
  if (value === "order_created") return "Sipariş Oluşturuldu";
  if (value === "delivered") return "Teslim Edildi";
  if (value === "cancelled") return "İptal Edildi";

  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export default class PendingOrderDetailScreen extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      orderRequest: props.location.state?.orderRequest,
      loading: !props.location.state?.orderRequest,
      loadError: false,
      actionsAnchorEl: null,
      cancelDialogOpen: false,
      cancelReason: "",
      actionLoading: false,
    };
  }

  componentDidMount() {
    this.loadOrderRequestById();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      const orderRequest = this.props.location.state?.orderRequest;

      this.setState(
        {
          orderRequest,
          loading: !orderRequest,
          loadError: false,
        },
        () => {
          if (!orderRequest) this.loadOrderRequestById();
        },
      );
    }
  }

  async loadOrderRequestById() {
    const targetId = this.props.match.params.id;

    this.setState({ loading: true, loadError: false });

    try {
      const response = await API.fetch("getpendingorderrequest", {
        _id: targetId,
      });

      if (response?.orderRequest && !response?.error) {
        this.setState({ orderRequest: response.orderRequest, loading: false });
        return;
      }

      this.setState({ loadError: true, loading: false });
    } catch (error) {
      this.setState({ loadError: true, loading: false });
    }
  }

  closeActionsMenu = () => {
    this.setState({ actionsAnchorEl: null });
  };

  openCancelDialog = () => {
    this.setState({
      cancelDialogOpen: true,
      cancelReason: this.state.orderRequest?.cancelReason || "",
      actionsAnchorEl: null,
    });
  };

  closeCancelDialog = () => {
    if (this.state.actionLoading) return;

    this.setState({ cancelDialogOpen: false, cancelReason: "" });
  };

  async cancelOrderRequest() {
    const { orderRequest, cancelReason } = this.state;

    if (!orderRequest?._id) return;

    const trimmedReason = cancelReason.trim();
    const body = {
      _id: orderRequest._id,
    };

    if (trimmedReason) body.reason = trimmedReason;

    this.setState({ actionLoading: true });
    global.showLoadingPanel();

    try {
      const response = await API.fetch("cancelorderrequest", body);

      if (response?.error) {
        global.showPopup("İşlem Başarısız", response.error);
        return;
      }

      const now = new Date().toISOString();

      this.setState((prevState) => ({
        orderRequest: {
          ...prevState.orderRequest,
          status: response?.status || "cancelled",
          cancelReason: trimmedReason,
          cancelledAt: prevState.orderRequest?.cancelledAt || now,
          updatedAt: now,
        },
        cancelDialogOpen: false,
        cancelReason: "",
      }));
    } finally {
      global.hideLoadingPanel();
      this.setState({ actionLoading: false });
    }
  }

  deleteOrderRequest() {
    const { orderRequest } = this.state;

    if (!orderRequest?._id) return;

    this.closeActionsMenu();

    global.openPrompt(
      "Talep silinsin mi?",
      "Henüz gerçek siparişe dönüşmemiş bu talep kalıcı olarak silinecek.",
      "Sil",
      async () => {
        this.setState({ actionLoading: true });
        global.showLoadingPanel();

        try {
          const response = await API.fetch("deleteorderrequest", {
            _id: orderRequest._id,
          });

          if (response?.error) {
            global.showPopup("İşlem Başarısız", response.error);
            return;
          }

          this.props.history.push("/pending-orders");
        } finally {
          global.hideLoadingPanel();
          this.setState({ actionLoading: false });
        }
      },
    );
  }

  render() {
    const { orderRequest, loading, loadError } = this.state;

    if ((orderRequest?.jyskWorkflowEnabled === true || (orderRequest?.jyskWorkflowEnabled === undefined && ["T003", "T011"].includes(orderRequest?.store_id || orderRequest?.store_reference_id))) && !loading) return <div><Button onClick={() => this.props.history.push("/pending-orders")} sx={{ mb: 2 }}>← Taleplere dön</Button><Typography variant="h4" sx={{ mb: 3 }}>Teslimat Talebi #{getShortId(orderRequest._id)}</Typography><SectionCard><Typography>{orderRequest.full_name} · {orderRequest.phone} · {orderRequest.email}</Typography><Typography>{orderRequest.store_label} · {orderRequest.delivery_address?.label}</Typography></SectionCard><JyskRequestOperations requestId={orderRequest._id} onUpdated={() => this.loadOrderRequestById()} /></div>;

    if (loading) {
      return (
        <div
          style={{
            minHeight: 360,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </div>
      );
    }

    if (!orderRequest) {
      return (
        <div>
          <Typography variant="h4" style={{ marginBottom: 20 }}>
            Sipariş Talebi Detayı
          </Typography>
          <SectionCard>
            <Typography variant="h6" style={{ marginBottom: 12 }}>
              Talep verisi bulunamadı
            </Typography>
            <Typography
              style={{ color: "rgb(101, 116, 139)", marginBottom: 20 }}
            >
              {loadError
                ? "Kayıt yenilenirken veri alınamadı. Lütfen tekrar deneyin veya listeye dönün."
                : "Bu detay ekranı için kayıt bulunamadı. Lütfen listeden tekrar açın."}
            </Typography>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={() => this.props.history.push("/pending-orders")}
              >
                Listeye dön
              </Button>
              {loadError && (
                <Button
                  variant="outlined"
                  onClick={() => this.loadOrderRequestById()}
                >
                  Tekrar dene
                </Button>
              )}
            </div>
          </SectionCard>
        </div>
      );
    }

    const receiptImages = (orderRequest.receipt_images || []).filter(
      (image) => image?.base64,
    );

    return (
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 24,
            flexWrap: "wrap",
            marginBottom: 26,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="h3"
                style={{ fontWeight: 700, lineHeight: 1.1 }}
              >
                Sipariş #{getShortId(orderRequest._id)}
              </Typography>

              <div
                style={{
                  backgroundColor: "#24479A",
                  color: "white",
                  borderRadius: 4,
                  padding: "5px 10px",
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                {orderRequest.store_label || "STORE"}
              </div>

              <Typography
                style={{
                  fontWeight: 700,
                  color: "#24479A",
                  fontSize: 18,
                }}
              >
                {orderRequest.store_reference_id || ""}
              </Typography>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginTop: 12,
                flexWrap: "wrap",
              }}
            >
              <Typography style={{ fontSize: 16 }}>sipariş_tarihi:</Typography>
              <div
                style={{
                  backgroundColor: "#EFF1F5",
                  borderRadius: 999,
                  padding: "7px 12px",
                }}
              >
                <Typography style={{ fontWeight: 600, fontSize: 14 }}>
                  {formatDate(orderRequest.createdAt)}
                </Typography>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <IconButton
              onClick={(event) =>
                this.setState({ actionsAnchorEl: event.currentTarget })
              }
              sx={{
                width: 42,
                height: 42,
                borderRadius: "10px",
                backgroundColor: "#EFF1F5",
                color: "#334155",
                "&:hover": {
                  backgroundColor: "#E2E8F0",
                },
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <IconButton
              onClick={() => this.props.history.push("/pending-orders")}
              sx={{
                width: 56,
                height: 40,
                borderRadius: "10px",
                backgroundColor: "#5654E8",
                color: "white",
                "&:hover": {
                  backgroundColor: "#4D4AD8",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </div>
        </div>

        <Menu
          anchorEl={this.state.actionsAnchorEl}
          open={Boolean(this.state.actionsAnchorEl)}
          onClose={this.closeActionsMenu}
        >
          {orderRequest.status !== "cancelled" && (
            <MenuItem onClick={this.openCancelDialog}>İptal Et</MenuItem>
          )}
          {orderRequest.status !== "order_created" && !orderRequest.orderId && (
            <MenuItem onClick={() => this.deleteOrderRequest()}>
              Talebi Sil
            </MenuItem>
          )}
        </Menu>

        <SectionCard style={{ padding: 0 }}>
          <SectionHeader>Detaylar</SectionHeader>
          <DetailRow
            first
            label="Müşteri"
            value={orderRequest.full_name || "-"}
          />
          <DetailRow label="Telefon" value={orderRequest.phone || "-"} />
          <DetailRow label="E-posta" value={orderRequest.email || "-"} />
          <DetailRow label="Mağaza" value={orderRequest.store_label || "-"} />
          <DetailRow
            label="Mağaza Referans"
            value={orderRequest.store_reference_id || "-"}
          />
          <DetailRow
            label="Durum"
            value={
              <StatusBadge status={orderRequest.status}>
                {getStatusLabel(orderRequest.status)}
              </StatusBadge>
            }
          />
          <DetailRow label="Kaynak" value={orderRequest.source || "-"} />
          <DetailRow
            label="Sipariş ID"
            value={orderRequest.orderId || "-"}
            action={
              orderRequest.orderId ? (
                <Link
                  component={RouterLink}
                  to={`/order/${orderRequest.orderId}`}
                  underline="hover"
                  style={{ fontWeight: 600, color: "#5654E8" }}
                >
                  Panelde Aç
                </Link>
              ) : null
            }
          />
          <DetailRow label="Not" value={orderRequest.note || "-"} />
          <DetailRow
            label="İptal Nedeni"
            value={orderRequest.cancelReason || "-"}
          />
          <DetailRow
            label="Teslimat Adresi"
            value={orderRequest.delivery_address?.label || "-"}
          />
          <DetailRow
            label="Bina / Kat / Daire"
            value={`${orderRequest.delivery_address?.building || "-"} · ${
              orderRequest.delivery_address?.floor || "-"
            } · ${orderRequest.delivery_address?.unit || "-"}`}
          />
          <DetailRow
            label="Koordinat"
            value={
              orderRequest.delivery_address?.lat != null &&
              orderRequest.delivery_address?.lng != null
                ? `${orderRequest.delivery_address.lat}, ${orderRequest.delivery_address.lng}`
                : "-"
            }
            action={
              orderRequest.delivery_address?.lat != null &&
              orderRequest.delivery_address?.lng != null ? (
                <Link
                  href={`https://www.google.com/maps?q=${orderRequest.delivery_address.lat},${orderRequest.delivery_address.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  underline="hover"
                  style={{ fontWeight: 600, color: "#5654E8" }}
                >
                  Haritada Aç
                </Link>
              ) : null
            }
          />
          <DetailRow
            label="Barkod Özeti"
            value={getBarcodeSummary(orderRequest.barcodes)}
          />
          <DetailRow
            label="Fiş Görselleri"
            value={`${receiptImages.length} adet`}
          />
          <DetailRow
            label="Oluşturma"
            value={formatDate(orderRequest.createdAt)}
          />
          <DetailRow
            label="Güncelleme"
            value={formatDate(orderRequest.updatedAt)}
          />
          <DetailRow
            label="İptal Tarihi"
            value={formatDate(orderRequest.cancelledAt)}
          />
        </SectionCard>

        <div
          style={{
            gridTemplateColumns: "minmax(0, 1.1fr) minmax(320px, 0.9fr)",
            gap: 24,
            marginTop: 24,
          }}
        >
          <SectionCard style={{ padding: 0 }}>
            <SectionHeader>Ürün Barkodları</SectionHeader>
            {(orderRequest.barcodes || []).length === 0 && (
              <div style={{ padding: 24 }}>
                <Typography style={{ color: "rgb(101, 116, 139)" }}>
                  Barkod kaydı bulunmuyor.
                </Typography>
              </div>
            )}

            {(orderRequest.barcodes || []).map((barcode, index) => (
              <BarcodeRow
                key={`${barcode.barcode}-${index}`}
                barcode={barcode}
                isLast={index === (orderRequest.barcodes || []).length - 1}
              />
            ))}
          </SectionCard>
        </div>
        <SectionCard style={{ marginTop: 20 }}>
          <SectionHeader>Fiş Görselleri</SectionHeader>
          {receiptImages.length === 0 && (
            <div style={{ padding: 24 }}>
              <Typography style={{ color: "rgb(101, 116, 139)" }}>
                Fiş görseli bulunmuyor.
              </Typography>
            </div>
          )}

          <div
            style={{
              padding: 20,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
            }}
          >
            {receiptImages.map((image, index) => {
              const src = getReceiptSrc(image);

              return (
                <div
                  key={`${image.name || "receipt"}-${index}`}
                  style={{
                    border: "1px solid #E6E8F0",
                    borderRadius: 8,
                    padding: 10,
                  }}
                >
                  <img
                    src={src}
                    alt={image.name || `receipt-${index + 1}`}
                    onClick={() =>
                      global.openCarousel(
                        receiptImages.map((receipt) => getReceiptSrc(receipt)),
                        index,
                      )
                    }
                    style={{
                      width: "100%",
                      height: 170,
                      objectFit: "cover",
                      borderRadius: 6,
                      cursor: "pointer",
                      backgroundColor: "#F8FAFC",
                    }}
                  />

                  <Typography
                    style={{
                      marginTop: 10,
                      fontWeight: 600,
                      fontSize: 14,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={getReceiptDisplayName(image, index)}
                  >
                    {getReceiptDisplayName(image, index)}
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{
                      color: "#65748B",
                      display: "block",
                      marginTop: 4,
                    }}
                  >
                    {image.mime_type || "-"} · {formatFileSize(image.size)}
                  </Typography>
                </div>
              );
            })}
          </div>
        </SectionCard>

        <Dialog
          open={this.state.cancelDialogOpen}
          onClose={() => this.closeCancelDialog()}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Talebi iptal et</DialogTitle>
          <DialogContent>
            <Typography
              style={{ color: "#64748B", fontSize: 14, marginBottom: 16 }}
            >
              İptal nedeni müşteri detayında gösterilecek.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="İptal nedeni"
              placeholder="Customer changed mind"
              value={this.state.cancelReason}
              onChange={(event) =>
                this.setState({ cancelReason: event.target.value })
              }
              multiline
              minRows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.closeCancelDialog()}
              disabled={this.state.actionLoading}
            >
              Vazgeç
            </Button>
            <Button
              onClick={() => this.cancelOrderRequest()}
              disabled={this.state.actionLoading}
              variant="contained"
              color="error"
            >
              İptal Et
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

const SectionCard = (props) => {
  return (
    <div
      style={{
        backgroundColor: "white",
        display: "flex",
        flexDirection: "column",
        boxShadow:
          "rgb(100 116 139 / 6%) 0px 1px 1px, rgb(100 116 139 / 10%) 0px 1px 2px",
        borderRadius: 8,
        color: "rgb(18, 24, 40)",
        overflow: "hidden",
        ...props.style,
      }}
    >
      {props.children}
    </div>
  );
};

const SectionHeader = ({ children }) => (
  <div
    style={{
      padding: "20px 24px",
      borderBottom: "1px solid #E6E8F0",
      backgroundColor: "white",
    }}
  >
    <Typography variant="h6" style={{ fontWeight: 700 }}>
      {children}
    </Typography>
  </div>
);

const DetailRow = ({ label, value, action, first }) => (
  <div
    style={{
      borderTop: first ? "none" : "1px solid #E6E8F0",
      padding: "14px 24px",
      display: "grid",
      gridTemplateColumns: "180px minmax(0, 1fr) auto",
      gap: 18,
      alignItems: "center",
    }}
  >
    <Typography style={{ fontWeight: 700, fontSize: 15 }}>{label}</Typography>
    {typeof value === "string" || typeof value === "number" ? (
      <Typography
        style={{
          fontSize: 15,
          color: "#1F2937",
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
        }}
      >
        {value}
      </Typography>
    ) : (
      value
    )}
    <div>{action}</div>
  </div>
);

const StatusBadge = ({ status, children }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 32,
      width: "fit-content",
      padding: "0 14px",
      borderRadius: 999,
      backgroundColor:
        STATUS_STYLES[status]?.backgroundColor || "rgba(86, 84, 232, 0.14)",
      color: STATUS_STYLES[status]?.color || "#4338CA",
      fontWeight: 700,
      fontSize: 13,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </div>
);

const BarcodeRow = ({ barcode, isLast }) => (
  <div
    style={{
      padding: "16px 24px",
      borderBottom: isLast ? "none" : "1px solid #E6E8F0",
      display: "flex",
      justifyContent: "space-between",
      gap: 16,
      alignItems: "center",
    }}
  >
    <div
      style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}
    >
      <BarcodeProductImage barcode={barcode.barcode} />
      <div style={{ minWidth: 0 }}>
        <Typography style={{ fontWeight: 700, fontSize: 15 }}>
          {barcode.product_name || "Ürün adı yok"}
        </Typography>
        <Typography style={{ color: "#65748B", marginTop: 4, fontSize: 14 }}>
          {barcode.barcode || "-"}
        </Typography>
        <Typography style={{ color: "#65748B", marginTop: 4, fontSize: 14 }}>
          {barcode.quantity || "-"}
        </Typography>
      </div>
    </div>

    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: barcode.found ? "rgb(19, 161, 123)" : "rgb(209, 67, 67)",
        flexShrink: 0,
      }}
    >
      {barcode.found ? (
        <CheckCircleIcon fontSize="small" />
      ) : (
        <CancelIcon fontSize="small" />
      )}
      <Typography style={{ fontWeight: 700, fontSize: 14 }}>
        {barcode.found ? "Bulundu" : "Bulunamadı"}
      </Typography>
    </div>
  </div>
);

class BarcodeProductImage extends React.Component {
  constructor(props) {
    super(props);

    this.state = { hasError: false };
  }

  render() {
    const { barcode } = this.props;
    const imageSrc = getBarcodeImageSrc(barcode);

    return (
      <div
        style={{
          width: 72,
          height: 72,
          minWidth: 72,
          borderRadius: 10,
          backgroundColor: "#F8FAFC",
          border: "1px solid #E6E8F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {!this.state.hasError && imageSrc ? (
          <img
            src={imageSrc}
            alt={barcode || "Ürün görseli"}
            onError={() => this.setState({ hasError: true })}
            style={{
              width: 64,
              height: 64,
              objectFit: "contain",
            }}
          />
        ) : (
          <Typography
            style={{
              color: "#94A3B8",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            Görsel
          </Typography>
        )}
      </div>
    );
  }
}

const formatFileSize = (size) => {
  if (!size && size !== 0) return "-";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};
